import axios from 'axios';
import { awsClient } from './awsClient';
import { normalizeVideo } from './content';

const contentBasePath = '/content';

/** APIが返すエラーコードを画面に出す日本語に置き換える。 */
const API_ERROR_MESSAGES = {
  unauthorized: 'ログインの有効期限が切れています。再ログインしてください。',
  forbidden: 'admin 権限が必要です。',
  admin_role_required: 'admin 権限が必要です。再ログインしても解決しない場合は権限設定を確認してください。',
  upload_id_invalid: 'アップロードの識別子が不正です。最初からやり直してください。',
  title_required: '動画タイトルを入力してください。',
  title_too_long: '動画タイトルが長すぎます。',
  category_id_required: 'カテゴリを選択してください。',
  questionnaire_url_required: 'アンケートURLを入力してください。',
  questionnaire_url_too_long: 'アンケートURLが長すぎます。',
  questionnaire_url_invalid: 'アンケートURLは http:// または https:// で始まるURLを入力してください。',
  category_not_found: '選択したカテゴリが見つかりませんでした。カテゴリ一覧を再読み込みしてください。',
  category_sequence_exhausted:
    'このカテゴリの連番（999）を使い切りました。カテゴリ構成の見直しが必要です。',
  object_key_outside_upload: 'アップロード先が不正です。最初からやり直してください。',
  uploaded_object_not_found:
    'アップロードしたファイルが見つかりませんでした。もう一度アップロードしてください。',
  video_required: '動画ファイルを選択してください。',
  video_extension_not_allowed: '動画ファイルの形式が対応していません。',
  video_too_large: '動画ファイルのサイズが上限を超えています。',
  thumbnail_required: 'サムネイル画像を選択してください。',
  thumbnail_extension_not_allowed: 'サムネイル画像の形式が対応していません。',
  thumbnail_too_large: 'サムネイル画像のサイズが上限を超えています。',
  material_extension_not_allowed: '配布資料の形式が対応していません。',
  material_too_large: '配布資料のサイズが上限を超えています。',
  material_required: '配布資料のファイルを選択してください。',
  material_file_name_required: '配布資料のファイル名を取得できませんでした。選び直してください。',
  media_bucket_not_configured: 'S3バケットの設定（MEDIA_BUCKET）がされていません。',
  mediaconvert_role_arn_not_configured:
    '動画変換の設定（MEDIACONVERT_ROLE_ARN）がされていません。AWS 側の設定を確認してください。',
  mediaconvert_sdk_not_available:
    'Lambda に @aws-sdk/client-mediaconvert が配置されていません。レイヤーの追加が必要です。',
  s3_access_denied:
    'S3への権限が不足しています。Lambdaのロールに s3:GetObject / s3:ListBucket があるか確認してください。',
  transcoded_object_not_found: '変換後の動画ファイルが見つかりませんでした。',
  public_id_reservation_failed: '動画識別子の採番に失敗しました。時間をおいて再実行してください。',
  video_id_invalid: '対象動画の指定が不正です。一覧を再読み込みしてください。',
  video_not_found: '対象動画が見つかりませんでした。一覧を再読み込みしてください。',
  is_published_invalid: '公開状態の指定が不正です。',
  video_publish_update_failed: 'DBの更新に失敗しました。公開状態は変更されていません。',
  video_title_update_failed: 'DBの更新に失敗しました。タイトルは変更されていません。',
  video_materials_update_failed:
    'DBの更新に失敗しました。配布資料・アンケートURLは変更されていません。',
  tag_ids_invalid: 'タグの指定が不正です。画面を開き直してください。',
  tag_not_found: '選択したタグが見つかりませんでした。タグ候補を再検索してください。',
  tag_not_active: '無効化されたタグは新しく付与できません。',
  too_many_tags: '1本の動画に付けられるタグ数の上限を超えています。',
  video_ids_invalid: '並び替えの指定が不正です。画面を再読み込みしてください。',
  video_ids_duplicated: '並び替えの指定に同じ動画が重複しています。画面を再読み込みしてください。',
  video_order_mismatch:
    'カテゴリ内の動画構成が変わっています。画面を再読み込みしてから並び替えてください。',
  category_videos_not_found: '選択したカテゴリに動画がありません。',
  too_many_videos: '1カテゴリの動画数が上限を超えています。',
  video_sort_order_update_failed: 'DBの更新に失敗しました。並び順は変更されていません。',
  not_found: '対象が見つかりませんでした。一覧を再読み込みしてください。',
  internal_server_error: 'サーバー側の処理に失敗しました。時間をおいて再実行してください。',
};

export function toAdminApiErrorMessage(error, fallback = '処理に失敗しました。') {
  if (!error) return fallback;

  const data = error.response?.data;
  const code = String(data?.error ?? '').trim();
  if (code && API_ERROR_MESSAGES[code]) return API_ERROR_MESSAGES[code];

  const detail = String(data?.detail ?? '').trim();
  if (code && detail) return `${fallback}（${code}: ${detail}）`;
  if (code) return `${fallback}（${code}）`;
  if (error.message) return `${fallback}（${error.message}）`;
  return fallback;
}

/** 変換ジョブの失敗理由。API はサーバー側のエラーメッセージをそのまま返す。 */
export function toJobErrorMessage(rawError) {
  const raw = String(rawError ?? '').trim();
  if (!raw) return '動画の登録に失敗しました。';
  if (API_ERROR_MESSAGES[raw]) return API_ERROR_MESSAGES[raw];
  if (raw.startsWith('transcode_')) return `動画の変換に失敗しました。（${raw}）`;
  if (raw.startsWith('finalize_failed')) return `S3格納・DB登録に失敗しました。（${raw}）`;
  return `動画の登録に失敗しました。（${raw}）`;
}

export async function listCategories() {
  const response = await awsClient.get(`${contentBasePath}/categories`);
  const items = Array.isArray(response.data?.items) ? response.data.items : [];

  return items
    .map((item) => ({
      id: item.id,
      slug: item.slug ?? null,
      name: item.name ?? item.slug ?? `Category ${item.id}`,
      description: item.description ?? null,
      sort_order: Number(item.sort_order ?? 0) || 0,
    }))
    .filter((item) => item.id != null);
}

/**
 * 署名付きPUT URLを発行する。
 * @param {{ video: object, thumbnail: object, material?: object|null }} payload
 */
export async function createVideoUploadTargets(payload) {
  const response = await awsClient.post(`${contentBasePath}/admin/videos/uploads`, payload);
  return response.data;
}

export function fileUploadMeta(file) {
  if (!file) return null;
  return {
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    size: file.size,
  };
}

/**
 * S3への直接PUT。`awsClient` は Authorization ヘッダを付けてしまい署名が壊れるため、
 * 素の axios を使う。
 */
export async function putFileToPresignedUrl({ url, file, contentType, onProgress, signal }) {
  await axios.put(url, file, {
    headers: { 'Content-Type': contentType || file.type || 'application/octet-stream' },
    signal,
    transformRequest: [(data) => data],
    onUploadProgress: (event) => {
      if (!onProgress) return;
      const total = Number(event.total ?? file.size ?? 0);
      if (total > 0) onProgress(Math.min(1, Number(event.loaded ?? 0) / total));
    },
  });
}

/** 識別子の採番と変換ジョブの投入。完了はポーリングで待つ。 */
export async function startVideoRegistration(payload) {
  const response = await awsClient.post(`${contentBasePath}/admin/videos`, payload);
  return response.data;
}

export async function getVideoUploadJob(jobId) {
  const response = await awsClient.get(
    `${contentBasePath}/admin/videos/jobs/${encodeURIComponent(jobId)}`
  );
  return response.data;
}

/** 処理中・最近のジョブ。ブラウザを閉じたあとの状況確認用。 */
export async function listVideoUploadJobs(params = {}) {
  const response = await awsClient.get(`${contentBasePath}/admin/videos/jobs`, { params });
  const items = Array.isArray(response.data?.items) ? response.data.items : [];
  return items.map((item) => ({
    jobId: item.jobId ?? null,
    publicId: item.publicId ?? null,
    title: item.title ?? '',
    status: item.status ?? 'unknown',
    error: item.error ?? null,
    videoId: item.videoId ?? null,
    shareUrl: item.shareUrl ?? null,
    createdAt: item.createdAt ?? null,
    updatedAt: item.updatedAt ?? null,
  }));
}

/**
 * 管理画面の動画一覧。公開側の `/content/videos` と違い非公開の動画も含む。
 *
 * `sort` に渡せる値:
 * - `newest` / `oldest`: `created_at` の降順 / 昇順
 * - `order_desc` / `order_asc`: `sort_order` の降順 / 昇順（カテゴリ指定時の並び）
 * - 未指定: `sort_order asc, id asc`
 */
export async function listAdminVideos(params = {}) {
  const response = await awsClient.get(`${contentBasePath}/admin/videos`, { params });
  const items = Array.isArray(response.data?.items) ? response.data.items : [];

  return {
    items: items.map(normalizeVideo),
    pagination: response.data?.pagination ?? null,
  };
}

/**
 * 公開 / 非公開の切り替え。DB更新が成功したときだけ解決するので、
 * 呼び出し側はこの結果を待ってから一覧の表示を変えてよい。
 */
export async function setAdminVideoPublishState(videoId, isPublished) {
  const response = await awsClient.post(
    `${contentBasePath}/admin/videos/${encodeURIComponent(videoId)}/publish`,
    { isPublished: Boolean(isPublished) }
  );

  return {
    id: response.data?.id ?? videoId,
    isPublished: Boolean(response.data?.isPublished),
    updatedAt: response.data?.updatedAt ?? null,
  };
}

/**
 * タイトルの変更。`title` と `updated_at` だけが更新される。
 * `public_id` / `slug` / S3のObject Key は変わらないので共有用URLも維持される。
 */
export async function setAdminVideoTitle(videoId, title) {
  const response = await awsClient.post(
    `${contentBasePath}/admin/videos/${encodeURIComponent(videoId)}/title`,
    { title }
  );

  return {
    id: response.data?.id ?? videoId,
    title: response.data?.title ?? title,
    updatedAt: response.data?.updatedAt ?? null,
  };
}

/**
 * 配布資料1件分の署名付きPUT URLを発行する。差し替え専用。
 * 新規追加の `createVideoUploadTargets` は動画とサムネイルが必須なので別のエンドポイントを使う。
 */
export async function createVideoMaterialUploadTarget(material) {
  const response = await awsClient.post(`${contentBasePath}/admin/videos/materials/uploads`, {
    material,
  });
  return response.data;
}

/** 対象動画に現在登録されている配布資料とアンケートURL。 */
export async function getAdminVideoMaterials(videoId) {
  const response = await awsClient.get(
    `${contentBasePath}/admin/videos/${encodeURIComponent(videoId)}/materials`
  );
  return normalizeAdminVideoMaterials(response.data, videoId);
}

/**
 * 配布資料・アンケートURLの変更。
 * `materialObjectKey` を渡さないときは資料に触らず、アンケートURLだけを更新する。
 */
export async function setAdminVideoMaterials(
  videoId,
  { questionnaireUrl, uploadId = null, materialObjectKey = null }
) {
  const response = await awsClient.post(
    `${contentBasePath}/admin/videos/${encodeURIComponent(videoId)}/materials`,
    { questionnaireUrl, uploadId, materialObjectKey }
  );
  return normalizeAdminVideoMaterials(response.data, videoId);
}

function normalizeAdminVideoMaterials(data, videoId) {
  const items = Array.isArray(data?.materials) ? data.materials : [];

  return {
    videoId: data?.videoId ?? data?.id ?? videoId,
    // 資料の実体は署名付きURL。画面からそのまま開いて中身を確認できる。
    materials: items
      .map((item) => ({ name: item?.name || 'Download', url: item?.url ?? null }))
      .filter((item) => item.url),
    questionnaireUrl: data?.questionnaireUrl ?? data?.questionnaire_url ?? null,
    updatedAt: data?.updatedAt ?? null,
  };
}

/** タグ候補の検索。`is_active = 1` のタグだけが返る。 */
export async function searchAdminTags(keyword) {
  const response = await awsClient.get(`${contentBasePath}/admin/tags`, {
    params: { q: keyword },
  });
  const items = Array.isArray(response.data?.items) ? response.data.items : [];
  return items.map(normalizeAdminTag);
}

/** 対象動画に現在付与されているタグ。無効化済みのタグも含まれる。 */
export async function getAdminVideoTags(videoId) {
  const response = await awsClient.get(
    `${contentBasePath}/admin/videos/${encodeURIComponent(videoId)}/tags`
  );
  const items = Array.isArray(response.data?.items) ? response.data.items : [];
  return items.map(normalizeAdminTag);
}

/**
 * タグ構成の保存。`tagIds` は「保存後にあるべき全量」を渡す。
 * 差分の INSERT / DELETE は Lambda 側が1トランザクションで行う。
 */
export async function setAdminVideoTags(videoId, tagIds) {
  const response = await awsClient.post(
    `${contentBasePath}/admin/videos/${encodeURIComponent(videoId)}/tags`,
    { tagIds }
  );
  const items = Array.isArray(response.data?.items) ? response.data.items : [];

  return {
    videoId: response.data?.videoId ?? videoId,
    items: items.map(normalizeAdminTag),
    added: Number(response.data?.added ?? 0) || 0,
    removed: Number(response.data?.removed ?? 0) || 0,
  };
}

function normalizeAdminTag(raw) {
  return {
    id: Number(raw?.id),
    name: raw?.name ?? raw?.slug ?? `Tag ${raw?.id}`,
    slug: raw?.slug ?? null,
    description: raw?.description ?? null,
    // 列が無い環境では API 側が true を返す
    isActive: raw?.isActive !== false,
  };
}

/**
 * カテゴリ内の全動画。並び替えはページを跨げないので全件を集める。
 * `sort` を渡さないと API 側は `sort_order asc, id asc` で返す。
 */
export async function listAllAdminVideos(params = {}) {
  const pageSize = params.pageSize ?? 100;
  const firstPage = await listAdminVideos({ ...params, page: 1, pageSize });
  const total = Number(firstPage.pagination?.total ?? firstPage.items.length);
  const totalPages = Math.ceil(total / pageSize);

  if (totalPages <= 1) return firstPage;

  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      listAdminVideos({ ...params, page: index + 2, pageSize })
    )
  );

  return {
    items: firstPage.items.concat(remainingPages.flatMap((page) => page.items)),
    pagination: firstPage.pagination,
  };
}

/**
 * カテゴリ内の並び替えを保存する。`videoIds` は新しい表示順の全件。
 * Lambda 側がそのカテゴリで今使われている `sort_order` を昇順で割り当て直すので、
 * 値の集合（＝最大値や隙間）は変わらない。
 */
export async function setAdminVideoSortOrder(categoryId, videoIds) {
  const response = await awsClient.post(`${contentBasePath}/admin/videos/sort-order`, {
    categoryId,
    videoIds,
  });

  return {
    categoryId: response.data?.categoryId ?? categoryId,
    items: Array.isArray(response.data?.items) ? response.data.items : videoIds,
    updated: Number(response.data?.updated ?? 0) || 0,
  };
}
